import React from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./index.scss";
import ja from "date-fns/locale/ja";
import moment from "moment";

registerLocale("ja", ja);

const CustomDatePicker = React.forwardRef((props, ref) => {
  const {
    className,
    option,
    name,
    onChange,
    dataName,
    showTimeSelect,
    timeIntervals,
    id,
    dateFormat,
    showTimeByWorkingTime,
    ...otherProps
  } = props;

  const user = window.store.getState().auth.user;
  const { workingHoursOfWeek, booking_time_block } = user;

  const [selectedDate, setSelectedDate] = React.useState(null); // Date
  const [selectedTime, setSelectedTime] = React.useState(""); // "HH:mm"
  const [includeTime, setIncludeTime] = React.useState([]);

  // Init from option
  React.useEffect(() => {
    if (option?.selectedDate) {
      const dateObj = new Date(option.selectedDate);
      setSelectedDate(new Date(dateObj));
      setSelectedTime(moment(dateObj).format("HH:mm"));
    } else {
      setSelectedDate(null);
      setSelectedTime("");
    }
  }, [option?.selectedDate]);

  // Generate time list when selectedDate changes
  React.useEffect(() => {
    if (showTimeByWorkingTime && selectedDate) {
      const times = getIncludeTime(selectedDate);
      setIncludeTime(times);
    }
  }, [selectedDate]);

  const getIncludeTime = (date) => {
    const { startTime, endTime } = getWorkingTime(date);
    const list = [];
    if (!startTime || !endTime) return [];
    const current = moment(startTime);
    while (current.isSameOrBefore(endTime)) {
      list.push(current.format("HH:mm"));
      current.add(booking_time_block, "minutes");
    }
    return list;
  };

  const getWorkingTime = (date) => {
    const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    const workingDay = workingHoursOfWeek.find(
      (item) => item.dayOfWeek === days[date.getDay()]
    );

    let startTime = null;
    let endTime = null;

    if (workingDay?.workingHours?.length) {
      const workingTime = workingDay.workingHours[0];
      startTime = `${moment(date).format("YYYY-MM-DD")} ${convertStringToTime(
        workingTime.start
      )}`;
      endTime = `${moment(date).format("YYYY-MM-DD")} ${convertStringToTime(
        workingTime.end
      )}`;
    }

    return {
      startTime,
      endTime,
    };
  };

  const convertStringToTime = (time) => {
    if (typeof time !== "string") return "";
    if (time.includes(":")) return time;
    return time.slice(0, 2) + ":" + time.slice(2);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (selectedTime) {
      triggerOnChange(date, selectedTime);
    }
  };

  const handleTimeChange = (e) => {
    const timeStr = e.target.value; // "HH:mm"
    setSelectedTime(timeStr);
    if (selectedDate) {
      triggerOnChange(selectedDate, timeStr);
    }
  };

  const triggerOnChange = (date, timeStr) => {
    const [h, m] = timeStr.split(":").map(Number);
    const merged = moment(date).hour(h).minute(m).second(0).toDate();
    onChange(merged); // final callback
  };

  return (
    <div
      className={`custom-react-datepicker-container w-full d-flex gap-2 ${
        option?.disabled ? "cursor-default" : "cursor-pointer"
      }`}
    >
      {/* Date Picker */}
      <DatePicker
        id={id + "-date"}
        data-name={dataName}
        name={name}
        locale="ja"
        className={`${className} w-70`}
        selected={selectedDate}
        onChange={handleDateChange}
        dateFormat={dateFormat?.split(" ")[0] || "yyyy-MM-dd"}
        placeholderText={option?.placeholder}
        maxDate={new Date(9999, 11, 31)}
        disabled={!!option?.disabled}
        autoComplete="off"
        {...otherProps}
      />

      {/* Time Select */}
      {showTimeSelect && (
        <select
          className="form-select custom-time-select"
          style={{
            width: 90,
            maxHeight: 100,
          }}
          value={selectedTime}
          onChange={handleTimeChange}
        >
          {includeTime.length ? (
            includeTime.map((timeStr) => (
              <option key={timeStr} value={timeStr}>
                {timeStr}
              </option>
            ))
          ) : (
            <option key={"empty"} value="">
              休日
            </option>
          )}
        </select>
      )}
    </div>
  );
});

CustomDatePicker.displayName = "CustomDatePicker";
export default CustomDatePicker;
